import type * as mapboxgl from 'mapbox-gl';
import { debugGroupOutlines, debugPartFeatures, WHISP_DEBUG_COLORS, WhispDebugView } from './whisp-debug';

export const WHISP_DEBUG_PART_SOURCE = 'whisp-debug-part-src';
export const WHISP_DEBUG_GROUP_SOURCE = 'whisp-debug-group-src';
export const WHISP_DEBUG_FILL = 'whisp-debug-fill';
export const WHISP_DEBUG_LINE = 'whisp-debug-line';
export const WHISP_DEBUG_SELECTED = 'whisp-debug-selected';
export const WHISP_DEBUG_GROUP_LINE = 'whisp-debug-group-line';
export const WHISP_DEBUG_GROUP_LABEL = 'whisp-debug-group-label';

const LAYERS = [
  WHISP_DEBUG_FILL,
  WHISP_DEBUG_LINE,
  WHISP_DEBUG_SELECTED,
  WHISP_DEBUG_GROUP_LINE,
  WHISP_DEBUG_GROUP_LABEL,
];

const EMPTY = { type: 'FeatureCollection' as const, features: [] };

const riskColor = [
  'match',
  ['get', 'risk'],
  'low',
  WHISP_DEBUG_COLORS.low,
  'more_info_needed',
  WHISP_DEBUG_COLORS.more_info_needed,
  'high',
  WHISP_DEBUG_COLORS.high,
  WHISP_DEBUG_COLORS.error,
] as unknown as mapboxgl.ExpressionSpecification;

export class WhispDebugLayerController {
  private attached = false;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly onSelect: (partId: string) => void
  ) {}

  show(view: WhispDebugView, visible: boolean): void {
    this.attach();
    this.source(WHISP_DEBUG_PART_SOURCE)?.setData({ type: 'FeatureCollection', features: debugPartFeatures(view) });
    this.source(WHISP_DEBUG_GROUP_SOURCE)?.setData({ type: 'FeatureCollection', features: debugGroupOutlines(view) });
    this.setVisible(visible);
  }

  clear(): void {
    if (!this.attached) return;
    this.source(WHISP_DEBUG_PART_SOURCE)?.setData(EMPTY);
    this.source(WHISP_DEBUG_GROUP_SOURCE)?.setData(EMPTY);
    this.select(null);
  }

  select(partId: string | null): void {
    if (!this.attached) return;
    this.map.setFilter(WHISP_DEBUG_SELECTED, ['==', ['get', 'partId'], partId ?? '']);
  }

  setVisible(visible: boolean): void {
    if (!this.attached) return;
    LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none'));
  }

  private attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.map.addSource(WHISP_DEBUG_PART_SOURCE, { type: 'geojson', data: EMPTY });
    this.map.addSource(WHISP_DEBUG_GROUP_SOURCE, { type: 'geojson', data: EMPTY });
    this.map.addLayer({
      id: WHISP_DEBUG_FILL,
      type: 'fill',
      source: WHISP_DEBUG_PART_SOURCE,
      paint: { 'fill-color': riskColor, 'fill-opacity': 0.35 },
    });
    this.map.addLayer({
      id: WHISP_DEBUG_LINE,
      type: 'line',
      source: WHISP_DEBUG_PART_SOURCE,
      paint: { 'line-color': riskColor, 'line-width': 2 },
    });
    this.map.addLayer({
      id: WHISP_DEBUG_SELECTED,
      type: 'line',
      source: WHISP_DEBUG_PART_SOURCE,
      filter: ['==', ['get', 'partId'], ''],
      paint: { 'line-color': '#00e5ff', 'line-width': 4 },
    });
    this.map.addLayer({
      id: WHISP_DEBUG_GROUP_LINE,
      type: 'line',
      source: WHISP_DEBUG_GROUP_SOURCE,
      paint: { 'line-color': '#ffffff', 'line-width': 1.5, 'line-dasharray': [3, 2] },
    });
    this.map.addLayer({
      id: WHISP_DEBUG_GROUP_LABEL,
      type: 'symbol',
      source: WHISP_DEBUG_GROUP_SOURCE,
      layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'symbol-placement': 'line' },
      paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1 },
    });
    this.map.on('click', WHISP_DEBUG_FILL, (event) => {
      const partId = event.features?.[0]?.properties?.['partId'];
      if (typeof partId === 'string') this.onSelect(partId);
    });
  }

  private source(id: string): mapboxgl.GeoJSONSource | undefined {
    return this.map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
  }
}
